import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Backup as BackupIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { FormBuilder, FormField } from "../../../shared/components/FormBuilder";
import { SystemSettings } from "../types";

interface SystemSettingsProps {
  settings: SystemSettings | undefined;
  isLoading: boolean;
  isUpdating: boolean;
  error: string | undefined;
  onUpdate: (data: Partial<SystemSettings>) => Promise<void>;
  onRefresh: () => void;
}

export const SystemSettingsComponent: React.FC<SystemSettingsProps> = ({
  settings,
  isLoading,
  isUpdating,
  error,
  onUpdate,
  onRefresh,
}) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Sistem ayarları form alanları
  const settingsFields: FormField[] = [
    {
      name: "system_name",
      label: "Sistem Adı",
      type: "text",
      required: true,
      validation: { min: 2, max: 100 },
    },
    {
      name: "system_version",
      label: "Sistem Versiyonu",
      type: "text",
      required: true,
    },
    {
      name: "maintenance_mode",
      label: "Bakım Modu",
      type: "switch",
      required: false,
    },
    {
      name: "registration_enabled",
      label: "Kayıt Olmaya İzin Ver",
      type: "switch",
      required: false,
    },
    {
      name: "email_notifications",
      label: "E-posta Bildirimleri",
      type: "switch",
      required: false,
    },
    {
      name: "sms_notifications",
      label: "SMS Bildirimleri",
      type: "switch",
      required: false,
    },
    {
      name: "default_language",
      label: "Varsayılan Dil",
      type: "select",
      required: true,
      options: [
        { value: "tr", label: "Türkçe" },
        { value: "en", label: "İngilizce" },
        { value: "de", label: "Almanca" },
        { value: "fr", label: "Fransızca" },
      ],
    },
    {
      name: "default_timezone",
      label: "Varsayılan Saat Dilimi",
      type: "select",
      required: true,
      options: [
        { value: "Europe/Istanbul", label: "Türkiye (UTC+3)" },
        { value: "UTC", label: "UTC" },
        { value: "Europe/London", label: "Londra (UTC+0)" },
        { value: "America/New_York", label: "New York (UTC-5)" },
        { value: "Asia/Tokyo", label: "Tokyo (UTC+9)" },
      ],
    },
    {
      name: "session_timeout",
      label: "Oturum Zaman Aşımı (dakika)",
      type: "number",
      required: true,
      validation: { min: 5, max: 1440 },
    },
    {
      name: "max_login_attempts",
      label: "Maksimum Giriş Denemesi",
      type: "number",
      required: true,
      validation: { min: 3, max: 10 },
    },
    {
      name: "password_min_length",
      label: "Minimum Şifre Uzunluğu",
      type: "number",
      required: true,
      validation: { min: 6, max: 20 },
    },
    {
      name: "require_strong_password",
      label: "Güçlü Şifre Gerektir",
      type: "switch",
      required: false,
    },
    {
      name: "backup_frequency",
      label: "Yedekleme Sıklığı",
      type: "select",
      required: true,
      options: [
        { value: "daily", label: "Günlük" },
        { value: "weekly", label: "Haftalık" },
        { value: "monthly", label: "Aylık" },
      ],
    },
    {
      name: "backup_retention_days",
      label: "Yedek Saklama Süresi (gün)",
      type: "number",
      required: true,
      validation: { min: 7, max: 365 },
    },
  ];

  const handleEditSubmit = async (data: Record<string, any>) => {
    try {
      await onUpdate(data);
      setEditDialogOpen(false);
    } catch (error) {
      console.error("Sistem ayarları güncellenirken hata:", error);
    }
  };

  const getStatusColor = (status: boolean) => {
    return status ? "success" : "error";
  };

  const getStatusIcon = (status: boolean) => {
    return status ? <CheckIcon /> : <ErrorIcon />;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography>Sistem ayarları yükleniyor...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Genel Sistem Ayarları */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Genel Sistem Ayarları"
              action={
                <Box>
                  <Tooltip title="Yenile">
                    <IconButton
                      onClick={onRefresh}
                      disabled={isLoading}
                      size="small"
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => setEditDialogOpen(true)}
                    disabled={isLoading || isUpdating}
                    sx={{ ml: 1 }}
                  >
                    Düzenle
                  </Button>
                </Box>
              }
            />
            <CardContent>
              {settings ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      {settings.system_name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Versiyon: {settings.system_version}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Bakım Modu
                    </Typography>
                    <Chip
                      icon={getStatusIcon(settings.maintenance_mode)}
                      label={settings.maintenance_mode ? "Aktif" : "Pasif"}
                      color={getStatusColor(settings.maintenance_mode)}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Kayıt Olmaya İzin
                    </Typography>
                    <Chip
                      icon={getStatusIcon(settings.registration_enabled)}
                      label={settings.registration_enabled ? "Aktif" : "Pasif"}
                      color={getStatusColor(settings.registration_enabled)}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Varsayılan Dil
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {settings.default_language === "tr"
                        ? "Türkçe"
                        : settings.default_language}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Varsayılan Saat Dilimi
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {settings.default_timezone}
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Typography color="error">
                  Sistem ayarları yüklenemedi
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Güvenlik Ayarları */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Güvenlik Ayarları" avatar={<SecurityIcon />} />
            <CardContent>
              {settings ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Oturum Zaman Aşımı
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {settings.session_timeout} dakika
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Maksimum Giriş Denemesi
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {settings.max_login_attempts}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Minimum Şifre Uzunluğu
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {settings.password_min_length} karakter
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Güçlü Şifre Gerektir
                    </Typography>
                    <Chip
                      icon={getStatusIcon(settings.require_strong_password)}
                      label={
                        settings.require_strong_password ? "Aktif" : "Pasif"
                      }
                      color={getStatusColor(settings.require_strong_password)}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Typography color="error">
                  Güvenlik ayarları yüklenemedi
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Bildirim Ayarları */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Bildirim Ayarları"
              avatar={<NotificationsIcon />}
            />
            <CardContent>
              {settings ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      E-posta Bildirimleri
                    </Typography>
                    <Chip
                      icon={getStatusIcon(settings.email_notifications)}
                      label={settings.email_notifications ? "Aktif" : "Pasif"}
                      color={getStatusColor(settings.email_notifications)}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      SMS Bildirimleri
                    </Typography>
                    <Chip
                      icon={getStatusIcon(settings.sms_notifications)}
                      label={settings.sms_notifications ? "Aktif" : "Pasif"}
                      color={getStatusColor(settings.sms_notifications)}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Typography color="error">
                  Bildirim ayarları yüklenemedi
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Yedekleme Ayarları */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Yedekleme Ayarları" avatar={<BackupIcon />} />
            <CardContent>
              {settings ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Yedekleme Sıklığı
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {settings.backup_frequency === "daily" && "Günlük"}
                      {settings.backup_frequency === "weekly" && "Haftalık"}
                      {settings.backup_frequency === "monthly" && "Aylık"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Yedek Saklama Süresi
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {settings.backup_retention_days} gün
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Typography color="error">
                  Yedekleme ayarları yüklenemedi
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sistem Durumu */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Sistem Durumu" />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="success.main">
                      {(settings as any)?.system_uptime || "99.9%"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sistem Uptime
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="info.main">
                      {(settings as any)?.average_response_time || "1.2s"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ortalama Yanıt Süresi
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="warning.main">
                      {(settings as any)?.storage_limit || "Sınırsız"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Disk Kullanımı
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary.main">
                      {(settings as any)?.ram_usage || "45%"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      RAM Kullanımı
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sistem Ayarları Düzenleme Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <SettingsIcon sx={{ mr: 1 }} />
            Sistem Ayarlarını Düzenle
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Sistem ayarlarını değiştirirken dikkatli olun. Bu değişiklikler tüm
            kullanıcıları etkileyebilir.
          </Alert>
          <FormBuilder
            fields={settingsFields}
            initialData={settings}
            onSubmit={handleEditSubmit}
            submitText="Güncelle"
            cancelText="İptal"
            loading={isUpdating}
            onCancel={() => setEditDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};
